import { JSX, useEffect, useRef, useState } from "react";
import { Document, DocumentStatus, FetchDocumentsByTenderIdQuery, FetchDocumentsByTenderIdQueryVariables, FetchDocumentsByTenderIdDocument, FetchTenderByIdQuery } from "@/graphql/__generated__/graphql";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { handleClickOutsideSource } from "./utils";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { AIChatThread } from "@/components/document-chat/chat-thread";
import DocumentViewer from "./DocumentViewer";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";
import { BookTextIcon } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import useUserStore from "@/store/shield";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { useQuery } from "@apollo/client/react";

interface DocumentChatComponentProps {
  variant?: "popover" | "page";
  tenderByIdDataContext?: FetchTenderByIdQuery;
}

/**
 * Document chat component that provides an AI-powered chat interface for tender documents.
 * Allows users to interact with documents through natural language queries and view document sources.
 *
 * Features a resizable layout with chat interface and optional document viewer panel.
 * Automatically detects if documents are available in the knowledge base and shows appropriate messaging.
 *
 * @component
 * @example
 * // Basic usage in tender edit layout
 * <DocumentChatComponent />
 *
 * @example
 * // Component automatically handles URL params for document viewing
 * // URL: /tender/123/chat?file=doc.pdf&page=1
 * <DocumentChatComponent />
 */
const DocumentChatComponent = ({ variant = "page", tenderByIdDataContext }: DocumentChatComponentProps): JSX.Element => {

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation(["components", "common"]);
  const { tenderID } = useParams<{ tenderID: string }>();
  const userId = useUserStore((state) => state.userId);
  const { tenderByIdData } = variant === "popover"
    ? { tenderByIdData: tenderByIdDataContext }
    : useOutletContext<EditTenderContext>();
  const [searchParams, _] = useSearchParams();

  // Refs
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // States
  const [showDocumentsView, setShowDocumentsView] = useState<boolean>(false);

  // Query hooks
  const { data: tenderData } = useQuery<FetchDocumentsByTenderIdQuery, FetchDocumentsByTenderIdQueryVariables>(FetchDocumentsByTenderIdDocument, {
    variables: { tenderId: Number(tenderID), status: DocumentStatus.InKnowledgeBase },
    fetchPolicy: "cache-and-network",
  });

  const tenderDocumentsData = tenderData?.fetchDocumentsByTenderId || [];
  const isTenderOwner = userId === tenderByIdData?.fetchTenderById.description.ownerId;

  /**
   * Checks if there are any documents in the knowledge base.
   *
   * @param documents - An array of Document objects to check.
   * @returns `true` if at least one document is in the knowledge base; otherwise, `false`.
   */
  const hasDocumentsInKnowledgeBase = (documents: Document[]): boolean => {
    return documents.some((document) => document.status === DocumentStatus.InKnowledgeBase);
  };

  // If there are search params, open the documents view
  useEffect(() => {
    if (searchParams.has("file") && searchParams.has("page")) {
      setShowDocumentsView(true);
    }
  }, [searchParams]);

  return (
    <div className={cn(variant === "popover" ? "h-full" : "h-fit pr-4 pl-2")} onClick={(e) => handleClickOutsideSource(e, navigate)}>

      <ResizablePanelGroup orientation="horizontal" className="h-full">
        <ResizablePanel defaultSize={showDocumentsView ? 60 : 100} minSize={20}>
          <div className="h-full">
            {/* Conversation screen */}
            {!hasDocumentsInKnowledgeBase(tenderDocumentsData) ? (
              <div className={cn(variant === "popover" ? "h-full" : "h-[calc(100vh-14rem)]", "flex flex-col overflow-y-auto justify-center px-10 text-center")}>
                <p className="text-lg font-semibold">
                  {t("documentchat.chat.chatunavailable")}
                </p>
                {isTenderOwner ? (
                  <div>
                    <p className="text-md text-muted-foreground">
                      {t("documentchat.chat.addtoknowledgebaseowner")}
                    </p>
                    <Button variant="default" className="mt-4" onClick={() => navigate(`/tender/${tenderID}/documents`)}>
                      {t("documentchat.chat.gotodocuments")}
                    </Button>
                  </div>
                ) : (
                  <p className="text-md text-muted-foreground">
                    {t("documentchat.chat.addtoknowledgebase")}
                  </p>
                )}
              </div>
            ) : (
              <div className={cn(variant === "popover" ? "h-full" : "h-[calc(100vh-6rem)]", showDocumentsView && "mr-2")} ref={scrollAreaRef}>
                <AIChatThread tenderDocumentsData={tenderDocumentsData} showDocumentsView={showDocumentsView} setShowDocumentsView={setShowDocumentsView} />
              </div>
            )}
          </div>
        </ResizablePanel>
        {(!showDocumentsView && hasDocumentsInKnowledgeBase(tenderDocumentsData)) && variant === "page" && (
          <div className="flex justify-end">
            <Collapsible open={showDocumentsView} onOpenChange={setShowDocumentsView}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CollapsibleTrigger asChild>
                    <Button variant="default" className="mt-2" size="icon">
                      <BookTextIcon className="size-4" />
                    </Button>
                  </CollapsibleTrigger>
                </TooltipTrigger>
                <TooltipContent side="left">
                  {t("documentchat.chat.showdocuments")}
                </TooltipContent>
              </Tooltip>
            </Collapsible>
          </div>
        )}
        {showDocumentsView && variant === "page" && (
          <>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={40} minSize={10}>
              <DocumentViewer isOpen={showDocumentsView} onClose={() => setShowDocumentsView(false)} />
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </div>
  );
};

export default DocumentChatComponent;
